import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import API from "../lib/api";
import { Users, Plus, Pencil, Ban, CheckCircle, Loader2, Shield, UserCheck } from "lucide-react";
import { ConnectionsCard, ConnectionsStatCard } from "../components/connections/ConnectionsLayout";
import { PageLayout } from "../components/PageLayout";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  audit_manager: "Audit Manager",
  senior_auditor: "Super Auditor",
  auditor: "Auditor",
  demo_admin: "Demo admin",
  external_auditor: "External Auditor",
  control_owner: "Control Owner",
  observer: "Observer",
};

const ROLE_COLORS = {
  super_admin: "bg-purple-100 text-purple-800 border-transparent dark:bg-purple-900/30 dark:text-purple-300",
  audit_manager: "bg-slate-100 text-slate-700 border-transparent dark:bg-slate-800/30 dark:text-slate-300",
  senior_auditor: "bg-blue-100 text-blue-700 border-transparent dark:bg-blue-900/30 dark:text-blue-300",
  auditor: "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/30 dark:text-slate-300 dark:border-slate-700",
  demo_admin: "bg-emerald-100 text-emerald-800 border-transparent dark:bg-emerald-900/30 dark:text-emerald-300",
  external_auditor: "bg-amber-100 text-amber-800 border-transparent dark:bg-amber-900/30 dark:text-amber-300",
  control_owner: "bg-slate-100 text-slate-700 border-transparent dark:bg-slate-800/30 dark:text-slate-300",
  observer: "bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/50 dark:text-zinc-300 dark:border-zinc-700"
};

const SUMMARY_ROLES = ["super_admin", "audit_manager", "demo_admin", "senior_auditor", "external_auditor", "control_owner"];

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "auditor", branch_code: "", department: "", process_areas_text: "" });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await API.get("/users");
      setUsers(data);
    } catch (e) { toast.error("Failed to load users"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error("All fields are required");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await API.post("/users", {
        email: newUser.email,
        name: newUser.name,
        password: newUser.password,
        role: newUser.role,
        branch_code: newUser.branch_code?.trim() || null,
        department: newUser.department || null,
        process_areas: String(newUser.process_areas_text || "").split(",").map(item => item.trim()).filter(Boolean),
      });
      setAddOpen(false);
      setNewUser({ email: "", name: "", password: "", role: manageableRoles[0]?.value || "auditor", branch_code: "", department: "", process_areas_text: "" });
      await fetchUsers();
      toast.success("User created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const payload = { name: editUser.name, role: editUser.role, status: editUser.status };
      if (editUser.newPassword) payload.password = editUser.newPassword;
      payload.branch_code = editUser.branch_code?.trim() || null;
      payload.department = editUser.department || "";
      payload.process_areas = String(editUser.process_areas_text || "").split(",").map(item => item.trim()).filter(Boolean);
      await API.put(`/users/${editUser.id}`, payload);
      setEditUser(null);
      await fetchUsers();
      toast.success("User updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "disabled" : "active";
    try {
      await API.put(`/users/${userId}`, { status: newStatus });
      await fetchUsers();
      toast.success(`User ${newStatus}`);
    } catch (e) { toast.error("Failed to update status"); }
  };

  const manageableRoles = (currentUser?.manageable_roles || []).map((role) => ({
    value: role,
    label: ROLE_LABELS[role] || role,
  }));

  return (
    <PageLayout
      icon={Users}
      title="User Management"
      subtitle="Manage auditor accounts, roles, access permissions, and governance clearance."
      actions={
        <Button
          size="sm"
          className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-4 shadow-md shadow-emerald-600/30 border-0 active:scale-95"
          onClick={() => setAddOpen(true)}
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add User
        </Button>
      }
      data-testid="user-management-page"
    >

      {/* Role Summary */}
      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {SUMMARY_ROLES.map((role) => {
          const label = ROLE_LABELS[role] || role;
          const count = users.filter(u => u.role === role && u.status === "active").length;
          const isHighlighted = count > 0;
          return (
            <ConnectionsStatCard
              key={role}
              value={count}
              label={label}
              icon={role === "super_admin" || role === "senior_auditor" ? Shield : role === "audit_manager" ? UserCheck : Users}
              tone={isHighlighted ? "red" : "neutral"}
            />
          );
        })}
      </div>

      {/* Users Table */}
      <ConnectionsCard title={`All Users (${users.length})`} className="user-table-card">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" /></div>
          ) : (
            <Table className="user-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="text-sm font-normal text-foreground">{u.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`user-role-badge text-xs ${ROLE_COLORS[u.role] || ''}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`user-status-badge ${u.status === 'active' ? "is-active" : "is-disabled"}`}>
                        <span className="mr-1.5 h-2 w-2 rounded-full bg-current" />
                        {u.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                          <Button variant="ghost" size="sm" className="h-8 w-8 rounded-lg p-0 text-primary hover:bg-blue-50"
                          onClick={() => setEditUser({ ...u, newPassword: "", process_areas_text: (u.process_areas || []).join(", ") })}
                          disabled={u.id === currentUser?.id && currentUser?.role !== "super_admin"}
                          data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {u.id !== currentUser?.id && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 rounded-lg p-0 text-red-500 hover:bg-red-50"
                            onClick={() => handleToggleStatus(u.id, u.status)}
                            data-testid={`toggle-user-${u.id}`}>
                            {u.status === 'active' ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </ConnectionsCard>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div><Label className="text-xs">Full Name</Label>
              <Input value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" className="mt-1" data-testid="new-user-name" /></div>
            <div><Label className="text-xs">Email</Label>
              <Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="john@bank.com" className="mt-1" data-testid="new-user-email" /></div>
            <div><Label className="text-xs">Password</Label>
              <Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" className="mt-1" data-testid="new-user-password" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {manageableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Branch Code (optional)</Label>
              <Input value={newUser.branch_code} onChange={e => setNewUser(p => ({ ...p, branch_code: e.target.value }))} placeholder="BR001" className="mt-1" /></div>
            <div><Label className="text-xs">Department</Label>
              <Input value={newUser.department} onChange={e => setNewUser(p => ({ ...p, department: e.target.value }))} placeholder="Finance, Operations..." className="mt-1" /></div>
            <div><Label className="text-xs">Process Areas</Label>
              <Input value={newUser.process_areas_text} onChange={e => setNewUser(p => ({ ...p, process_areas_text: e.target.value }))} placeholder="Payments, Returns" className="mt-1" /></div>
            <Button onClick={handleCreate} disabled={saving} className="w-full" data-testid="confirm-create-user-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3 pt-1">
              <div><Label className="text-xs">Full Name</Label>
                <Input value={editUser.name} onChange={e => setEditUser(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Email</Label>
                <Input value={editUser.email} disabled className="mt-1 font-mono opacity-60" /></div>
              {manageableRoles.length > 0 && (
                <div><Label className="text-xs">Role</Label>
                  <Select value={editUser.role} onValueChange={v => setEditUser(p => ({ ...p, role: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {manageableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
              <div><Label className="text-xs">Branch Code (optional)</Label>
                <Input value={editUser.branch_code || ""} onChange={e => setEditUser(p => ({ ...p, branch_code: e.target.value }))} className="mt-1" placeholder="BR001" /></div>
              <div><Label className="text-xs">Department</Label>
                <Input value={editUser.department || ""} onChange={e => setEditUser(p => ({ ...p, department: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Process Areas</Label>
                <Input value={editUser.process_areas_text || ""} onChange={e => setEditUser(p => ({ ...p, process_areas_text: e.target.value }))} className="mt-1" placeholder="Payments, Returns" /></div>
              <div><Label className="text-xs">New Password (leave blank to keep current)</Label>
                <Input type="password" value={editUser.newPassword || ""} onChange={e => setEditUser(p => ({ ...p, newPassword: e.target.value }))} className="mt-1" placeholder="New password..." /></div>
              <Button onClick={handleUpdate} disabled={saving} className="w-full" data-testid="confirm-edit-user-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
